import {eatalianoAxios} from "@/data/api/http/axios";
import {Category, Menu, Product} from "@/data/entities/menu";
import {OptionGroup, ProductOption} from "@/data/entities/option";
import {
    CategoryEditValues,
    OptionGroupEditValues,
    ProductEditValues,
    ProductOptionEditValues,
} from "@/data/entities/menuEditor";

// Menu editor (admin). Changes return the changed item only: refetch the menu
// for changes on other items (e.g. a new product is also in its categories).

/**
 * The complete menu with everything to edit, including the IDs.
 */
export const getMenu = (restaurantId: number): Promise<Menu> => (
    eatalianoAxios
        .get(`/admin/menu/${restaurantId}`)
        .then(response => response.data)
);

// Products

export const createProduct = (
    restaurantId: number,
    fields: ProductEditValues,
): Promise<Product> => (
    eatalianoAxios
        .post(`/admin/menu/${restaurantId}/products`, fields)
        .then(response => response.data)
);

export const updateProduct = (
    restaurantId: number,
    productId: number,
    fields: ProductEditValues,
): Promise<Product> => (
    eatalianoAxios
        .put(`/admin/menu/${restaurantId}/products/${productId}`, fields)
        .then(response => response.data)
);

/**
 * Deletes a product. It's removed from its categories, option groups, featured
 * products and open carts; its loyalty rewards are deactivated.
 */
export const deleteProduct = (
    restaurantId: number,
    productId: number,
): Promise<void> => (
    eatalianoAxios
        .delete(`/admin/menu/${restaurantId}/products/${productId}`)
        .then(() => undefined)
);

// Categories

export const updateCategory = (
    restaurantId: number,
    categoryId: number,
    fields: CategoryEditValues,
): Promise<Category> => (
    eatalianoAxios
        .put(`/admin/menu/${restaurantId}/categories/${categoryId}`, fields)
        .then(response => response.data)
);

// Option groups

/**
 * Creates an option group without options. Link it to products via the
 * product update.
 */
export const createOptionGroup = (
    restaurantId: number,
    fields: OptionGroupEditValues,
): Promise<OptionGroup> => (
    eatalianoAxios
        .post(`/admin/menu/${restaurantId}/option-groups`, fields)
        .then(response => response.data)
);

export const updateOptionGroup = (
    restaurantId: number,
    groupId: number,
    fields: OptionGroupEditValues,
): Promise<OptionGroup> => (
    eatalianoAxios
        .put(`/admin/menu/${restaurantId}/option-groups/${groupId}`, fields)
        .then(response => response.data)
);

/**
 * Deletes an option group. It's removed from the products; its options stay.
 */
export const deleteOptionGroup = (
    restaurantId: number,
    groupId: number,
): Promise<void> => (
    eatalianoAxios
        .delete(`/admin/menu/${restaurantId}/option-groups/${groupId}`)
        .then(() => undefined)
);

// Options

/**
 * Creates an option at the end of an option group.
 */
export const addOption = (
    restaurantId: number,
    groupId: number,
    fields: ProductOptionEditValues,
): Promise<ProductOption> => (
    eatalianoAxios
        .post(`/admin/menu/${restaurantId}/option-groups/${groupId}/options`, fields)
        .then(response => response.data)
);

/**
 * Removes an option from an option group. The option itself stays.
 */
export const removeOption = (
    restaurantId: number,
    groupId: number,
    optionId: number,
): Promise<void> => (
    eatalianoAxios
        .delete(`/admin/menu/${restaurantId}/option-groups/${groupId}/options/${optionId}`)
        .then(() => undefined)
);

/**
 * Updates an option, in all groups it's in.
 */
export const updateOption = (
    restaurantId: number,
    optionId: number,
    fields: ProductOptionEditValues,
): Promise<ProductOption> => (
    eatalianoAxios
        .put(`/admin/menu/${restaurantId}/options/${optionId}`, fields)
        .then(response => response.data)
);